/**
 * Qrew HR — Inner Page Design System
 * Reusable UI primitives for consistent premium SaaS look across all HRM modules.
 */
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  TrendingUp, TrendingDown, Search, Filter as FilterIcon,
  MoreHorizontal, ChevronDown, X,
} from "lucide-react";

/* =====================================================
 * BUTTON PRIMITIVES (use Qrew theme CSS variables)
 * Variants: primary | secondary | soft | danger | success | warning | ghost
 * Sizes:    sm | md | lg | icon-sm | icon
 * ===================================================== */
const VARIANT_CLS = {
  primary:   "btn-primary",
  secondary: "btn-secondary",
  soft:      "btn-soft",
  danger:    "btn-danger",
  success:   "btn-success",
  warning:   "btn-warning",
  ghost:     "btn-ghost",
};
const SIZE_CLS = {
  sm: "qh-btn-sm",
  md: "",
  lg: "qh-btn-lg",
  icon: "qh-btn-icon",
  "icon-sm": "qh-btn-icon qh-btn-sm",
};

export function QButton({
  variant = "primary",
  size = "md",
  icon: Icon,
  iconRight: IconRight,
  children,
  className = "",
  type = "button",
  ...rest
}) {
  const v = VARIANT_CLS[variant] || VARIANT_CLS.primary;
  const s = SIZE_CLS[size] || "";
  return (
    <button type={type} className={`qh-btn ${v} ${s} ${className}`} {...rest}>
      {Icon && <Icon className={size === "sm" || size === "icon-sm" ? "w-3.5 h-3.5" : "w-4 h-4"} />}
      {children}
      {IconRight && <IconRight className={size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4"} />}
    </button>
  );
}

// Aliases — semantic shortcuts
export const PrimaryButton   = (p) => <QButton variant="primary"   {...p} />;
export const SecondaryButton = (p) => <QButton variant="secondary" {...p} />;
export const SoftButton      = (p) => <QButton variant="soft"      {...p} />;
export const DangerButton    = (p) => <QButton variant="danger"    {...p} />;
export const SuccessButton   = (p) => <QButton variant="success"   {...p} />;
export const GhostButton     = (p) => <QButton variant="ghost"     {...p} />;
export const GradientButton  = (p) => <QButton variant="primary"   {...p} />;
export const IconButton      = ({ size = "icon", ...p }) => <QButton variant="secondary" size={size} {...p} />;

/* ============== APP PAGE (consistent padding wrapper) ============== */
export function AppPage({ children, className = "", testid = "app-page", maxWidth = null, fullBleed = false }) {
  const padding = fullBleed ? "" : "px-4 sm:px-6 lg:px-8";
  const max = maxWidth ? `max-w-[${maxWidth}]` : "";
  return (
    <div
      className={`py-5 sm:py-6 lg:py-7 ${padding} space-y-5 ${max} overflow-x-hidden ${className}`}
      data-testid={testid}
    >
      {children}
    </div>
  );
}

/* ============== PAGE HEADER ============== */
const HEADER_BADGE_TONES = {
  "Basic Version":     "bg-amber-100 text-amber-800 ring-amber-200",
  "Coming Soon":       "bg-slate-100 text-slate-700 ring-slate-200",
  "Beta":              "bg-violet-100 text-violet-800 ring-violet-200",
  "Backend Pending":   "bg-rose-100 text-rose-700 ring-rose-200",
  "Live":              "bg-emerald-100 text-emerald-700 ring-emerald-200",
  "New":               "bg-fuchsia-100 text-fuchsia-700 ring-fuchsia-200",
};

export function PageHeader({
  category, title, description, badge,
  primaryAction, secondaryAction, moreActions = [],
  testid = "page-header",
}) {
  const badgeCls = HEADER_BADGE_TONES[badge] || "bg-slate-100 text-slate-700 ring-slate-200";
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap" data-testid={testid}>
      <div className="min-w-0">
        {category && <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500">{category}</div>}
        <div className="flex items-center gap-2.5 flex-wrap mt-1">
          <h1 className="text-3xl font-bold font-[Outfit] tracking-tight text-slate-900 truncate">{title}</h1>
          {badge && (
            <span className={`inline-flex items-center gap-1 ${badgeCls} ring-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold`} data-testid={`${testid}-badge`}>
              {badge}
            </span>
          )}
        </div>
        {description && <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">{description}</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0 flex-wrap">
        {secondaryAction && (
          <SecondaryButton onClick={secondaryAction.onClick} icon={secondaryAction.icon} data-testid={secondaryAction.testid}>
            {secondaryAction.label}
          </SecondaryButton>
        )}
        {primaryAction && (
          <PrimaryButton onClick={primaryAction.onClick} icon={primaryAction.icon} data-testid={primaryAction.testid}>
            {primaryAction.label}
          </PrimaryButton>
        )}
        {moreActions.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="qh-btn btn-secondary qh-btn-icon" data-testid={`${testid}-more`}>
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {moreActions.map((a, i) => (
                <DropdownMenuItem key={i} onClick={a.onClick}>
                  {a.icon && <a.icon className="w-4 h-4 mr-2" />}
                  {a.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}

/* ============== KPI CARD ============== */
const KPI_TONES = {
  violet:  { bg: "from-violet-100 to-violet-50",   icon: "text-violet-600",   value: "text-violet-700"  },
  blue:    { bg: "from-blue-100 to-blue-50",       icon: "text-blue-600",     value: "text-blue-700"    },
  emerald: { bg: "from-emerald-100 to-emerald-50", icon: "text-emerald-600",  value: "text-emerald-700" },
  amber:   { bg: "from-amber-100 to-amber-50",     icon: "text-amber-600",    value: "text-amber-700"   },
  rose:    { bg: "from-rose-100 to-rose-50",       icon: "text-rose-600",     value: "text-rose-700"    },
  fuchsia: { bg: "from-fuchsia-100 to-fuchsia-50", icon: "text-fuchsia-600",  value: "text-fuchsia-700" },
  cyan:    { bg: "from-cyan-100 to-cyan-50",       icon: "text-cyan-600",     value: "text-cyan-700"    },
  slate:   { bg: "from-slate-100 to-slate-50",     icon: "text-slate-600",    value: "text-slate-700"   },
};

export function KPICard({ icon: Icon, label, value, sub, trend, trendDir, tone = "violet", footer, testid }) {
  const t = KPI_TONES[tone];
  return (
    <Card className="p-4 sm:p-5 border-slate-200 shadow-sm bg-white min-w-0" data-testid={testid}>
      <div className="flex items-start justify-between gap-2.5">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.12em] sm:tracking-[0.16em] font-bold text-slate-500 leading-tight break-words">{label}</div>
          <div className={`mt-1.5 sm:mt-2 text-2xl sm:text-3xl font-bold font-[Outfit] tracking-tight ${t.value} tabular-nums`}>{value}</div>
          {sub && <div className="text-[11px] sm:text-[12px] text-slate-500 mt-1 leading-snug break-words">{sub}</div>}
        </div>
        {Icon && (
          <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br ${t.bg} ${t.icon} flex items-center justify-center shrink-0 ring-1 ring-slate-100`}>
            <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
        )}
      </div>
      {footer}
      {trend !== undefined && (
        <div className={`mt-3 inline-flex items-center gap-1 text-[11px] font-semibold ${
          trendDir === "up" ? "text-emerald-600" : trendDir === "down" ? "text-rose-600" : "text-slate-500"
        }`}>
          {trendDir === "up" ? <TrendingUp className="w-3 h-3" /> : trendDir === "down" ? <TrendingDown className="w-3 h-3" /> : null}
          {trend}
        </div>
      )}
    </Card>
  );
}

/* ============== KPI GRID ============== */
export function KPIGrid({ cols = 5, children, testid = "kpi-grid" }) {
  const colClass = {
    3: "grid-cols-1 sm:grid-cols-3",
    4: "grid-cols-2 sm:grid-cols-2 lg:grid-cols-4",
    5: "grid-cols-2 md:grid-cols-3 lg:grid-cols-5",
    6: "grid-cols-2 md:grid-cols-3 lg:grid-cols-6",
  }[cols] || "grid-cols-2 lg:grid-cols-5";
  return <div className={`grid ${colClass} gap-3`} data-testid={testid}>{children}</div>;
}

/* ============== EMPTY STATE ============== */
export function EmptyState({ icon: Icon, title, message, action, testid = "empty-state" }) {
  return (
    <Card className="p-12 border-slate-200 shadow-sm bg-white text-center" data-testid={testid}>
      {Icon && (
        <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-100 to-fuchsia-50 text-violet-600 flex items-center justify-center mb-4 ring-1 ring-violet-100">
          <Icon className="w-8 h-8" />
        </div>
      )}
      <h3 className="text-lg font-bold font-[Outfit] text-slate-900">{title}</h3>
      {message && <p className="text-sm text-slate-500 mt-1.5 max-w-md mx-auto">{message}</p>}
      {action && (
        <PrimaryButton onClick={action.onClick} icon={action.icon} className="mt-5" data-testid={`${testid}-action`}>
          {action.label}
        </PrimaryButton>
      )}
    </Card>
  );
}

/* ============== FILTER BAR ============== */
export function FilterBar({ search, onSearch, searchPlaceholder = "Search…", filters = [], rightActions, testid = "filter-bar" }) {
  return (
    <div className="flex items-center gap-2.5 flex-wrap" data-testid={testid}>
      <div className="relative flex-1 min-w-[180px] max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <Input
          value={search || ""}
          onChange={(e) => onSearch?.(e.target.value)}
          placeholder={searchPlaceholder}
          className="pl-9 h-10 bg-white border-slate-200"
          data-testid={`${testid}-search`}
        />
        {search && (
          <button onClick={() => onSearch?.("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {filters.map((f, i) => (
        <select
          key={i}
          value={f.value || ""}
          onChange={(e) => f.onChange?.(e.target.value)}
          className="h-10 px-3 pr-8 rounded-lg border border-slate-200 text-[13px] font-semibold text-slate-700 bg-white"
          data-testid={f.testid || `${testid}-filter-${i}`}
        >
          <option value="">{f.label || "All"}</option>
          {(f.options || []).map((o) => (
            <option key={o.value || o} value={o.value || o}>{o.label || o}</option>
          ))}
        </select>
      ))}
      <Button variant="outline" size="icon" className="border-slate-200 shrink-0" data-testid={`${testid}-filter-btn`}>
        <FilterIcon className="w-4 h-4" />
      </Button>
      {rightActions && <div className="ml-auto flex items-center gap-2">{rightActions}</div>}
    </div>
  );
}

/* ============== STATUS BADGE ============== */
const STATUS_TONES = {
  active:      "bg-emerald-100 text-emerald-700",
  inactive:    "bg-slate-100 text-slate-700",
  pending:     "bg-amber-100 text-amber-700",
  in_progress: "bg-blue-100 text-blue-700",
  completed:   "bg-emerald-100 text-emerald-700",
  approved:    "bg-emerald-100 text-emerald-700",
  rejected:    "bg-rose-100 text-rose-700",
  draft:       "bg-slate-100 text-slate-700",
  open:        "bg-amber-100 text-amber-700",
  resolved:    "bg-emerald-100 text-emerald-700",
  high:        "bg-rose-100 text-rose-700",
  medium:      "bg-amber-100 text-amber-700",
  low:         "bg-slate-100 text-slate-700",
  trial:       "bg-amber-100 text-amber-700",
  suspended:   "bg-rose-100 text-rose-700",
};

export function StatusBadge({ status, tone, children, testid }) {
  const key = (status || "").toLowerCase().replace(/\s+/g, "_");
  const cls = tone ? `bg-${tone}-100 text-${tone}-700` : (STATUS_TONES[key] || "bg-slate-100 text-slate-700");
  return (
    <Badge className={`${cls} border-0 capitalize text-[10px] font-bold tracking-wider`} data-testid={testid}>
      {children || status?.replace(/_/g, " ")}
    </Badge>
  );
}

/* ============== AVATAR STACK ============== */
export function AvatarStack({ people = [], max = 4, size = 7, testid = "avatar-stack" }) {
  const visible = people.slice(0, max);
  const overflow = people.length - max;
  const sizeClass = { 6: "w-6 h-6 text-[9px]", 7: "w-7 h-7 text-[9px]", 8: "w-8 h-8 text-[10px]", 9: "w-9 h-9 text-[11px]" }[size] || "w-7 h-7 text-[9px]";
  const initials = (n = "U") => n.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");
  return (
    <div className={`flex -space-x-2`} data-testid={testid}>
      {visible.map((p, i) => (
        <Avatar key={p.id || i} className={`${sizeClass} ring-2 ring-white shadow-sm`}>
          {p.avatar_url && <AvatarImage src={p.avatar_url} alt={p.full_name || p.name} />}
          <AvatarFallback className="bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white font-bold">
            {initials(p.full_name || p.name)}
          </AvatarFallback>
        </Avatar>
      ))}
      {overflow > 0 && (
        <span className={`${sizeClass} rounded-full bg-slate-100 text-slate-600 font-bold ring-2 ring-white shadow-sm flex items-center justify-center`}>
          +{overflow}
        </span>
      )}
    </div>
  );
}

/* ============== SECTION CARD ============== */
export function SectionCard({ icon: Icon, title, sub, action, children, className = "", testid }) {
  return (
    <Card className={`p-5 border-slate-200 shadow-sm bg-white ${className}`} data-testid={testid}>
      {(title || action) && (
        <div className="flex items-center justify-between mb-4 gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            {Icon && (
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-100 to-fuchsia-50 text-violet-600 flex items-center justify-center ring-1 ring-violet-100 shrink-0">
                <Icon className="w-4 h-4" />
              </div>
            )}
            <div className="min-w-0">
              <h3 className="text-base font-bold font-[Outfit] text-slate-900 truncate">{title}</h3>
              {sub && <p className="text-[11px] text-slate-500 truncate">{sub}</p>}
            </div>
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
      )}
      {children}
    </Card>
  );
}

/* ============== TABS ROW (premium pill tabs) ============== */
export function PillTabs({ tabs = [], value, onChange, testid = "pill-tabs" }) {
  return (
    <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-slate-100" data-testid={testid}>
      {tabs.map((t) => {
        const active = value === t.value;
        return (
          <button
            key={t.value}
            onClick={() => onChange?.(t.value)}
            className={`px-4 h-9 rounded-lg text-[13px] font-semibold transition-all inline-flex items-center gap-1.5 ${
              active ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200" : "text-slate-600 hover:text-slate-900"
            }`}
            data-testid={`${testid}-${t.value}`}
          >
            {t.icon && <t.icon className="w-3.5 h-3.5" />}
            {t.label}
            {t.badge !== undefined && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-violet-100 text-violet-700" : "bg-slate-200 text-slate-600"}`}>{t.badge}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
